/**
 * EventDetailPage — one event, at `/event/events/:eventId`.
 *
 * Route: `/event/events/:eventId`. Reached from a calendar chip, a table row, and from
 * the create dialog's "Do It Later" exit.
 *
 * Follows the Figma's Event Detail Page (frames 1:9871 and 22:12431 — the second is the
 * just-created state). That design has five cards; four are built here:
 *
 *   Event Progress        built — EventProgress, driven by `lifecycleStage`
 *   Basic Information     built — the Figma's six fields, plus Event Sub Type, SFDC Campaign
 *                         ID, Event Hierarchy, Nomination Required / Time and Target (testing
 *                         feedback: the detail page showed none of what Edit Event sets).
 *                         Labels in English, matching the Edit Event form's
 *   Event Introduction    built — the event's `description`, rich text with images
 *                         (EventDescriptionView); shown only when there is one
 *   Attendance List       built — this event's nominations, then its campaign members, from
 *                         ListEventAttendees (members joined with their nominations)
 *   Details by BU         removed at PM request (testing doc #33): it counted nominations
 *                         only, and the dashboard below carries the per-BU picture
 *   Dashboard             built — the Octopus QuickSight lifecycle dashboard (the same one as
 *                         the Event Lifecycle Data tab), narrowed to this event through its
 *                         event parameter. The funnel and goal attainment are Octopus's data,
 *                         not something this app can plot, so it is embedded rather than
 *                         drawn; it sits last, below the cards computed here.
 *
 * The Attendance List reads every page of ListEventAttendees. ListNominations is not used here:
 * it backs the Nomination List page only.
 *
 * Fetches by id through GetEvent rather than reading a cached list: the URL is
 * shareable, so the page has to work on a cold load, and it should show the event as it
 * is now.
 *
 * One action sits beside the page title: `Edit Event`. It is what makes the create flow
 * finishable — "Do It Later" lands the user here with an event that has only a name, and
 * this is the way on to fill in the rest.
 *
 * Gated on role, as the service is: UpdateEvent / PublishEvent admit any marketer or admin on
 * any event (`canManageEvents`), not only the creator — changed at PM request so a marketer can
 * finish and publish a colleague's draft. Hidden from everyone else, for whom both are a 403.
 *
 * `Publish` sits beside it, and only while the event is a DRAFT. This is where the draft
 * ends — editing and publishing are separate decisions, so saving the form does not go
 * live. A marketer can fill in details over several sittings and publish when ready.
 *
 * The status also shows as a badge next to the title, so "why is there a Publish button"
 * and "what state is this in" have the same answer in the same glance.
 *
 * `Nominate` is the BD's action here (Event Hub Testing doc #50), on the same condition as the
 * home page's Nominate Now (utils/nominatableEvents `isNominatable`) and for the same roles the
 * service lets nominate (`canNominate`), so the two entry points cannot disagree about whether
 * an event is open — including the BU rule: only a BD whose BU is one of the event's sees it. It
 * goes to the nomination form with this event picked.
 *
 * A BD who is not also a marketer or admin (`seesOnlyOwnBuEvents`) sees only events for their own
 * BUs, as on the calendar: an event outside them — reached by a shared or typed link — shows a
 * "not available" notice instead of its content, as does an event with no BUs. The BUs come from
 * GET /me; while that loads the page waits, and if it fails nothing is shown (fail-closed). UX
 * only: GetEvent still returns the event, so the real guard is the service's.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Box,
  BreadcrumbGroup,
  Button,
  ColumnLayout,
  Container,
  ContentLayout,
  Header,
  SpaceBetween,
  StatusIndicator,
} from '@amzn/awsui-components-react';
import {
  canManageEvents,
  canNominate,
  isBd,
  seesOnlyOwnBuEvents,
} from '../../utils/authorization';
import { eventIsForBusinessUnits, isNominatable } from '../../utils/nominatableEvents';
import { useEventHubLogin } from '../../hooks/useEventHubLogin';
import { editHrefFor } from '../../utils/nominationEdit';
import { useEventHubRoles } from '../../hooks/useEventHubRoles';
import { EventProgress } from '../../components/EventProgress';
import { AttendanceList } from '../../components/AttendanceList';
import { NominationDetail } from '../../components/NominationDetail';
import QuickSightReport from '../../components/QuickSightReport/QuickSightReport';
import {
  EVENT_DETAIL_ACCOUNT,
  EVENT_DETAIL_DASHBOARD_ID,
  EVENT_DETAIL_EVENT_PARAM,
  EVENT_DETAIL_SHEET_ID,
} from '../../config/dashboards';
import { useEventAttendees } from '../../hooks/useEventAttendees';
import { useNominationBusinessUnits } from '../../hooks/useNominationBusinessUnits';
import { attendeeCsv } from '../../utils/attendees';
import { csvFilename, downloadCsv, toCsv } from '../../utils/csv';
import {
  eventHubCalendarClient,
  type Event,
  type EventAttendee,
  type Nomination,
} from '../../client/EventCalendarClient';
import { NONE, formatDateTime, formatRange } from '../../utils/eventDates';
import { subTypeLabel } from '../../utils/eventTaxonomy';
import {
  nominationRequiredLabel,
  nominationWindow,
  targetLines,
} from '../../utils/eventDetailFields';
import { loadHierarchy } from '../../components/CampaignHierarchy/hierarchySync';
import type { HierarchyNode } from '../../components/CampaignHierarchy/campaignHierarchy';
import { CampaignHierarchyView } from '../../components/CampaignHierarchy';
import { useEventHubLoading } from '../../components/EventHubLayout/EventHubLoading';
import { EventDescriptionView } from '../../components/EventDescriptionView';
import { isEmptyDescription } from '../../utils/eventDescription';

/**
 * One labelled field in the Basic Information grid. Labels are English (testing feedback),
 * under the same names the Edit Event form uses, though the Figma's are Chinese.
 */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      <div>{children}</div>
    </div>
  );
}

/** One line per entry, or a dash when there are none. */
function Lines({ lines }: { lines: readonly string[] }) {
  if (!lines.length) return <>{NONE}</>;
  return (
    <>
      {lines.map((line) => (
        <div key={line}>{line}</div>
      ))}
    </>
  );
}

/** Province + city, joined; either may be missing. */
function placeOf(event: Event): string {
  const parts = [event.province, event.city].filter(Boolean);
  return parts.length ? parts.join(' · ') : NONE;
}

/**
 * The draft/published axis as the Figma draws it: a green tick beside "Published". DRAFT
 * is not in the design (it only ever shows a published event), so it gets the pending
 * treatment — it is a state waiting on an action, not a neutral one.
 */
function statusOf(event: Event) {
  if (event.contentStatus === 'PUBLISHED') {
    return <StatusIndicator type="success">Published</StatusIndicator>;
  }
  if (event.contentStatus === 'DRAFT') {
    return <StatusIndicator type="pending">Draft</StatusIndicator>;
  }
  return <Box color="text-status-inactive">{NONE}</Box>;
}

export default function EventDetailPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();

  const roles = useEventHubRoles();
  const canManage = canManageEvents(roles);
  const { login } = useEventHubLogin();
  // The BUs an event must be for, for this caller to nominate to it (utils/nominatableEvents).
  const { businessUnits: bdBusinessUnits, loading: loadingBus } = useNominationBusinessUnits(
    canNominate(roles),
  );
  /* A BD-only reader waits for their BUs before anything of the event shows (see the header). */
  const buScoped = seesOnlyOwnBuEvents(roles);
  const waitingForBus = buScoped && loadingBus;

  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  /**
   * The campaign tree beneath this event: undefined while loading, null if it could not be
   * read. Its own fetch, apart from the event's — a failure costs one field, not the page.
   */
  const [hierarchy, setHierarchy] = useState<HierarchyNode[] | null | undefined>(undefined);
  /**
   * Why a publish attempt failed. Kept apart from the load error: the event is still on
   * screen and still readable, so this is a notice about one action rather than the page
   * having failed.
   */
  const [publishError, setPublishError] = useState<string | null>(null);
  /**
   * The nomination whose detail modal is open, or null. Plain state, not the URL: only the
   * Nomination List puts the open nomination in its URL (that is the page a link is shared
   * from); here the modal is a quick look that should leave the reader on this page.
   */
  const [viewing, setViewing] = useState<Nomination | null>(null);

  /* One fetch, two cards: every page of ListEventAttendees. The service decides what each role
     sees, so this card inherits that rule rather than inventing one. */
  const {
    attendees,
    loading: loadingAttendees,
    error: attendeesError,
    replaceNomination,
  } = useEventAttendees(eventId);
  // The overlay waits for the first page only; later pages arrive into the table behind it.
  const firstPagePending = loadingAttendees && attendees.length === 0;
  useEventHubLoading(loading || firstPagePending || waitingForBus);

  /** Stable across re-renders, so the embed doesn't re-apply the parameter on every one. */
  const dashboardEventKey = useMemo(() => (eventId ? [eventId] : []), [eventId]);

  /**
   * Exports what the card is showing.
   *
   * Built from the same values the table renders rather than from the raw nomination, so the
   * file and the screen cannot disagree — including the four columns with no field behind them
   * yet, which export as empty for the same reason they display as dashes.
   *
   * The rows come from the card, which owns the search box: exporting every attendee here
   * would ignore whatever the reader filtered to.
   */
  function onExportCsv(rowsToExport: readonly EventAttendee[]) {
    // Every Attendance List column, in its order and under its names (attendeeCsv).
    const { headers, rows } = attendeeCsv(rowsToExport);
    downloadCsv(
      csvFilename(`${event?.eventName ?? 'event'}-attendance`, 'attendance'),
      toCsv(headers, rows),
    );
  }

  useEffect(() => {
    if (!eventId) {
      // Only reachable by hand-editing the URL, but it must not fetch `undefined`.
      setError('No event id in the URL.');
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    setLoading(true);
    setError(null);

    eventHubCalendarClient()
      .getEvent(eventId, { signal: controller.signal })
      .then((result) => {
        if (cancelled) return;
        setEvent(result);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled || controller.signal.aborted) return;
        setEvent(null);
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [eventId]);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    setHierarchy(undefined);
    loadHierarchy(eventHubCalendarClient().campaignHierarchy(), eventId).then(
      (h) => {
        if (!cancelled) setHierarchy(h.nodes);
      },
      () => {
        if (!cancelled) setHierarchy(null);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  /**
   * Publishes, then shows the event the service handed back rather than re-reading it —
   * PublishEvent returns the updated resource, so a second round trip could only disagree
   * with what just happened.
   *
   * A rejection here is ordinary, not exceptional: the service refuses to publish an event
   * without dates, or a nomination-enabled one without its window, and that is exactly the
   * state an event created from a name alone is in. The message names the field to go and
   * fill in, so it is shown as-is.
   */
  async function onPublish() {
    if (!eventId) return;
    setPublishing(true);
    setPublishError(null);
    try {
      setEvent(await eventHubCalendarClient().publishEvent(eventId));
    } catch (e: unknown) {
      setPublishError(e instanceof Error ? e.message : String(e));
    } finally {
      setPublishing(false);
    }
  }

  /* An event outside the BD's BUs, or with none: shown as unavailable. `[]` (lookup failed)
     matches nothing, so a failure hides the event rather than revealing it. */
  const outsideBus =
    buScoped && !!event && !waitingForBus && !eventIsForBusinessUnits(event, bdBusinessUnits ?? []);
  /* The event as this reader may see it: withheld while their BUs load and when outside them. */
  const visibleEvent = waitingForBus || outsideBus ? null : event;

  const isDraft = visibleEvent?.contentStatus === 'DRAFT';
  const showNominate =
    !!event && canNominate(roles) && !loadingBus && isNominatable(event, new Date(), bdBusinessUnits);

  return (
    <ContentLayout
      header={
        <>
          <BreadcrumbGroup
            // Two levels, as the Figma draws them. The last crumb is the literal "Event
            // Detail Page" rather than the event's name, which is already the h1.
            items={[
              { text: 'Event Hub', href: '/event' },
              { text: 'Event Detail Page', href: '' },
            ]}
            onFollow={(e) => {
              e.preventDefault();
              if (e.detail.href) navigate(e.detail.href);
            }}
          />
          {/* The event's name is the page title. While it is still loading the heading
              is left empty rather than filled with "Loading…", so the title does not
              change twice. */}
          <Header
            variant="h1"
            /* Beside the title, as the Figma places it. Withheld until the event has
               loaded: an edit link for an event not yet on screen has nothing to edit. */
            actions={
              (canManage || showNominate) && visibleEvent ? (
                <SpaceBetween direction="horizontal" size="xs">
                  {canManage ? (
                    <Button onClick={() => navigate(`/event/events/${eventId}/edit`)}>
                      Edit Event
                    </Button>
                  ) : null}
                  {/* Only while it is a draft. Publishing something already published is not
                      something the service does, and the design has no Unpublish. */}
                  {canManage && isDraft ? (
                    <Button variant="primary" loading={publishing} onClick={onPublish}>
                      Publish
                    </Button>
                  ) : null}
                  {showNominate ? (
                    <Button
                      variant="primary"
                      onClick={() => navigate(`/event/nominations/new?eventId=${event.eventId}`)}
                    >
                      Nominate
                    </Button>
                  ) : null}
                </SpaceBetween>
              ) : undefined
            }
          >
            {/* The badge rides with the title, as the Figma draws it. The status is ALSO a
                Basic Information field below, which the design shows too — the badge answers
                "why is there a Publish button" at the point the button is, and the field
                answers "what are this event's attributes". */}
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              <span>{visibleEvent?.eventName ?? ''}</span>
              {isDraft ? <Badge color="blue">Draft</Badge> : null}
            </SpaceBetween>
          </Header>
        </>
      }
    >
      {publishError ? (
        <Box padding={{ bottom: 'l' }}>
          <Alert
            type="error"
            dismissible
            onDismiss={() => setPublishError(null)}
            header="Could not publish this event"
          >
            {publishError}
          </Alert>
        </Box>
      ) : null}

      {error ? (
        <Alert type="error" header="Failed to load this event">
          {error}
        </Alert>
      ) : loading || waitingForBus ? null /* the layout's overlay shows meanwhile */ : outsideBus ? (
        <Alert type="info" header="This event is not available">
          It is not for any of your business units.
        </Alert>
      ) : event ? (
        <>
          <EventProgress lifecycleStage={event.lifecycleStage} />
          <Box padding={{ top: 'l' }}>
            <Container header={<Header variant="h2">Basic Information</Header>}>
              {/* Four columns with dividers, matching the Figma's layout. */}
              <ColumnLayout columns={4} variant="text-grid">
                <Field label="Event Time">
                  {formatRange(event.startDate, event.endDate)}
                </Field>
                <Field label="Location">{placeOf(event)}</Field>
                <Field label="Status">{statusOf(event)}</Field>
                <Field label="BU">
                  {event.businessUnits?.length ? event.businessUnits.join(', ') : NONE}
                </Field>
                <Field label="Created By">{event.createdBy || NONE}</Field>
                <Field label="Created At">{formatDateTime(event.createdAt)}</Field>
                <Field label="Event Sub Type">{subTypeLabel(event.eventSubType)}</Field>
                <Field label="SFDC Campaign ID">{event.sfdcCampaignId || NONE}</Field>
                <Field label="Nomination Required">{nominationRequiredLabel(event)}</Field>
                <Field label="Nomination Time">{nominationWindow(event)}</Field>
                <Field label="Target">
                  <Lines lines={targetLines(event)} />
                </Field>
              </ColumnLayout>
              {/* Its own full-width row: a tree needs the width a grid cell does not have. */}
              <Box padding={{ top: 'l' }}>
                <Field label="Event Hierarchy">
                  {hierarchy === undefined ? (
                    <StatusIndicator type="loading">Loading</StatusIndicator>
                  ) : hierarchy === null ? (
                    <Box color="text-status-warning">Could not be loaded</Box>
                  ) : hierarchy.length ? (
                    <CampaignHierarchyView
                      eventName={event.eventName}
                      campaignId={event.sfdcCampaignId}
                      nodes={hierarchy}
                    />
                  ) : (
                    NONE
                  )}
                </Field>
              </Box>
            </Container>
          </Box>

          {/* Not in the Figma: the introduction marketers write on Edit Event. Hidden when there
              is none, rather than a card that only says so. */}
          {!isEmptyDescription(event.description) ? (
            <Box padding={{ top: 'l' }}>
              <Container header={<Header variant="h2">Event Introduction</Header>}>
                <EventDescriptionView html={event.description ?? ''} />
              </Container>
            </Box>
          ) : null}

          {/* Kept apart from the page's own load error: the event is on screen and readable,
              so this is a notice about two cards rather than a failed page. Said out loud
              because both cards' empty states otherwise read as "nobody has nominated yet",
              which is a different and reassuring claim. */}
          {attendeesError ? (
            <Box padding={{ top: 'l' }}>
              <Alert type="warning" header="Could not load this event's attendees">
                {attendeesError} — the Attendance List may be incomplete or
                empty because the list could not be read, not because there are none.
              </Alert>
            </Box>
          ) : null}

          <Box padding={{ top: 'l' }}>
            <AttendanceList
              attendees={attendees}
              loading={firstPagePending}
              loadingMore={loadingAttendees && attendees.length > 0}
              /* A BD's view is scoped by the service (their territories, their own and reports'
                 nominations); a marketer or admin sees the whole event. */
              scoped={isBd(roles) && !canManageEvents(roles)}
              onSelectNomination={setViewing}
              login={login}
              onNominationUpdated={replaceNomination}
              eventId={eventId}
              onOpenEvent={(id) => navigate(`/event/events/${id}`)}
              /* Hands off to the review queue rather than deciding here: Approve / Reject
                 already live there with the owner gate and the irreversible-decision dialog,
                 and a second entry point would be a second chance to disagree. */
              /* An event that takes no nominations has none to review. */
              onReview={
                event?.bdNominationSupported
                  ? () => navigate(`/event/nominations?eventId=${eventId}`)
                  : undefined
              }
              onExportCsv={onExportCsv}
            />
          </Box>

          {/* As on the Event Lifecycle Data tab: embedded per registered user, as the login Event
              Hub acts as, so Octopus's row-level security sees the right alias. The event comes
              from the URL — the dashboard keys Event Hub events by the same eventId. */}
          {eventId ? (
            <Box padding={{ top: 'l' }}>
              <Container header={<Header variant="h2">Dashboard by BU</Header>}>
                <QuickSightReport
                  dashboardId={EVENT_DETAIL_DASHBOARD_ID}
                  accountShortName={EVENT_DETAIL_ACCOUNT}
                  sheetId={EVENT_DETAIL_SHEET_ID}
                  useRegisteredUser
                  userAlias={login}
                  paramName={EVENT_DETAIL_EVENT_PARAM}
                  paramValues={dashboardEventKey}
                  height="800"
                />
              </Container>
            </Box>
          ) : null}

          {viewing ? (
            <NominationDetail
              nomination={viewing}
              eventName={event.eventName || eventId || ''}
              onDismiss={() => setViewing(null)}
              editHref={editHrefFor(viewing, event, login, new Date())}
            />
          ) : null}

        </>
      ) : null}
    </ContentLayout>
  );
}
